/**
 * FSC checkout – clones div[role="button"] into a <button> proxy for Route.
 *
 * Asset:  assets/fsc-checkout-route-proxy.js
 * Theme:  <script src="{{ 'fsc-checkout-route-proxy.js' | asset_url }}" defer></script>
 * Route checkout selector: #route-fsc-checkout-proxy
 */
(function () {
  'use strict';

  var ORIGINAL_SELECTOR =
    '#fsc-co-btn[role="button"], #fsc-co-btn, ' +
    '#fsc-checkout-link[role="button"], #fsc-checkout-link';
  var PROXY_ID = 'route-fsc-checkout-proxy';
  var HIDE_CLASS = 'route-fsc-checkout-sr-only';
  var STYLE_ID = 'route-fsc-checkout-proxy-styles';
  var DEBOUNCE_MS = 200;

  var SKIP_ATTRS = {
    id: true,
    role: true,
    tabindex: true,
    'aria-hidden': true,
    name: true,
    'data-route-ref': true,
    'data-route-copy': true,
    'data-route-cloned-button': true,
    'data-route-fsc-proxy-wired': true
  };

  var originalObserver = null;
  var debounceTimer = null;
  var lastSignature = '';
  var wiredOriginal = null;

  function injectStyles() {
    if (document.getElementById(STYLE_ID)) return;

    var style = document.createElement('style');
    style.id = STYLE_ID;
    style.textContent =
      '.' + HIDE_CLASS + ' {' +
      'position:absolute!important;' +
      'width:1px!important;' +
      'height:1px!important;' +
      'padding:0!important;' +
      'margin:-1px!important;' +
      'overflow:hidden!important;' +
      'clip:rect(0,0,0,0)!important;' +
      'clip-path:inset(50%)!important;' +
      'white-space:nowrap!important;' +
      'border:0!important;' +
      'opacity:0!important;' +
      'pointer-events:none!important;' +
      'z-index:-1!important;' +
      '}';
    (document.head || document.documentElement).appendChild(style);
  }

  function findOriginal() {
    return document.querySelector(ORIGINAL_SELECTOR);
  }

  /** Snapshot used to skip redundant proxy syncs. */
  function getSignature(el) {
    var classes = Array.from(el.classList)
      .filter(function (c) { return c !== HIDE_CLASS; })
      .join(' ');

    return classes + '|||' + (el.getAttribute('style') || '') + '|||' + el.innerHTML;
  }

  /** Visually hide the FSC div without display:none or visibility:hidden (Route-compatible). */
  function hideOriginal(original) {
    if (!original.classList.contains(HIDE_CLASS)) {
      original.classList.add(HIDE_CLASS);
    }
    original.setAttribute('aria-hidden', 'true');
    original.setAttribute('tabindex', '-1');
  }

  function shouldSkipAttr(name) {
    if (SKIP_ATTRS[name]) return true;
    if (name.indexOf('data-route') === 0) return true;
    return false;
  }

  /** Build a <button> from a deep clone of the FSC div. */
  function buttonFromDivClone(original) {
    var divClone = original.cloneNode(true);
    var button = document.createElement('button');

    button.type = 'button';
    button.id = PROXY_ID;

    Array.from(divClone.attributes).forEach(function (attr) {
      if (shouldSkipAttr(attr.name)) return;
      button.setAttribute(attr.name, attr.value);
    });

    button.classList.remove(HIDE_CLASS);
    button.innerHTML = divClone.innerHTML;

    return button;
  }

  /** Re-apply clone onto an existing proxy when the original changed. */
  function syncButtonFromDivClone(original, button) {
    var signature = getSignature(original);
    if (signature === lastSignature) return;

    var divClone = original.cloneNode(true);

    if (originalObserver) originalObserver.disconnect();

    Array.from(button.attributes).forEach(function (attr) {
      if (attr.name === 'id' || attr.name === 'type' || attr.name === 'data-route-fsc-proxy-wired') {
        return;
      }
      button.removeAttribute(attr.name);
    });

    Array.from(divClone.attributes).forEach(function (attr) {
      if (shouldSkipAttr(attr.name)) return;
      button.setAttribute(attr.name, attr.value);
    });

    button.type = 'button';
    button.id = PROXY_ID;
    button.classList.remove(HIDE_CLASS);
    button.innerHTML = divClone.innerHTML;

    lastSignature = signature;
    observeOriginal(original);
  }

  function triggerOriginalCheckout(original) {
    if (typeof original.click === 'function') {
      original.click();
      return;
    }
    original.dispatchEvent(
      new MouseEvent('click', { bubbles: true, cancelable: true, view: window })
    );
  }

  /** Forward proxy clicks to the hidden FSC div (skipped when Route owns the clone). */
  function wireProxyClick(proxy, original) {
    if (proxy.getAttribute('data-route-fsc-proxy-wired') === 'true') return;
    proxy.setAttribute('data-route-fsc-proxy-wired', 'true');

    proxy.addEventListener('click', function (e) {
      if (proxy.getAttribute('data-route-cloned-button') === 'true') return;
      e.preventDefault();
      e.stopImmediatePropagation();
      triggerOriginalCheckout(original);
    }, true);
  }

  /** Watch only the FSC div – not the whole document. */
  function observeOriginal(original) {
    if (originalObserver) originalObserver.disconnect();

    originalObserver = new MutationObserver(scheduleSetup);
    originalObserver.observe(original, {
      attributes: true,
      attributeFilter: ['class', 'style', 'disabled', 'aria-disabled'],
      childList: true,
      subtree: true
    });
  }

  /**
   * FSC sometimes replaces the checkout div (#fsc-co-btn / #fsc-checkout-link) with a new DOM node.
   * Drop stale proxy/state so setup() can create a fresh one.
   */
  function resetStateIfOriginalReplaced(original) {
    if (!wiredOriginal || wiredOriginal === original) return;

    var staleProxy = document.getElementById(PROXY_ID);
    if (staleProxy) staleProxy.remove();

    if (originalObserver) originalObserver.disconnect();
    originalObserver = null;
    lastSignature = '';
    wiredOriginal = null;
  }

  function scheduleSetup() {
    clearTimeout(debounceTimer);
    debounceTimer = setTimeout(setup, DEBOUNCE_MS);
  }

  function setup() {
    var original = findOriginal();
    if (!original) return;

    injectStyles();
    resetStateIfOriginalReplaced(original);
    hideOriginal(original);

    var proxy = document.getElementById(PROXY_ID);
    var signature = getSignature(original);

    if (!proxy || proxy.tagName !== 'BUTTON') {
      if (proxy) proxy.remove();

      proxy = buttonFromDivClone(original);
      lastSignature = signature;
      original.parentNode.insertBefore(proxy, original.nextSibling);
      wireProxyClick(proxy, original);
      wiredOriginal = original;
      observeOriginal(original);
      return;
    }

    wiredOriginal = original;

    if (signature !== lastSignature) {
      syncButtonFromDivClone(original, proxy);
    }

    wireProxyClick(proxy, original);
  }

  function start() {
    setup();

    // Late injection (e.g. cart drawer)
    setTimeout(setup, 500);
    setTimeout(setup, 2000);

    document.addEventListener('cart:updated', scheduleSetup);
    document.addEventListener('shopify:section:load', scheduleSetup);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
